import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { CatalogItem, DraftCatalog } from '@/lib/draft-order-client';

interface ItemPickerProps {
  catalog?: DraftCatalog;
  currencySymbol: string;
  disabled?: boolean;
  onSelect: (item: CatalogItem) => void;
}

/**
 * Searchable item picker, grouped by category.
 *
 * Built on the Radix popover already in the app rather than a command palette — `cmdk` is not a
 * dependency here and a filtered list is the same interaction for a menu this size. Sold-out items
 * stay visible but unselectable: an operator looking for something needs to be told it is out, not
 * left wondering whether they typed it wrong.
 */
export function ItemPicker({ catalog, currencySymbol, disabled, onSelect }: ItemPickerProps) {
  const { t } = useTranslation('conversations');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const categories = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (catalog?.categories ?? [])
      .map((category) => ({
        ...category,
        items: needle
          ? category.items.filter(
              (item) => item.name.toLowerCase().includes(needle) || item.description?.toLowerCase().includes(needle),
            )
          : category.items,
      }))
      .filter((category) => category.items.length > 0);
  }, [catalog, query]);

  const handleSelect = (item: CatalogItem) => {
    setOpen(false);
    setQuery('');
    onSelect(item);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="w-full justify-start" disabled={disabled}>
          <Plus className="h-3.5 w-3.5" />
          {t('cart.items.add')}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(22rem,calc(100vw-2rem))] p-0">
        <div className="border-b border-border p-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('cart.items.searchPlaceholder')}
              className="h-8 pl-8"
            />
          </div>
        </div>

        <div className="max-h-72 overflow-y-auto p-1">
          {categories.length === 0 && (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">{t('cart.items.noMatches')}</p>
          )}

          {categories.map((category) => (
            <div key={category.id} className="mb-1">
              <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {category.name}
              </p>
              {category.items.map((item) => {
                const unavailable = item.available === false;
                return (
                  <button
                    key={item.id}
                    type="button"
                    disabled={unavailable}
                    onClick={() => handleSelect(item)}
                    className={cn(
                      'flex w-full items-start justify-between gap-3 rounded-md px-2 py-1.5 text-left text-sm',
                      unavailable ? 'cursor-not-allowed opacity-50' : 'hover:bg-accent',
                    )}
                  >
                    <span className="min-w-0">
                      <span className={cn('block truncate text-foreground', unavailable && 'line-through')}>
                        {item.name}
                      </span>
                      {unavailable && (
                        <span className="block text-xs text-muted-foreground">{t('cart.items.unavailable')}</span>
                      )}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                      {currencySymbol}
                      {(item.promoPrice ?? item.price).toFixed(2)}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
